import { useTranslations } from 'next-intl';
import { memo, useCallback, useEffect } from 'react';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { type Activity, ActivityType } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';

interface SubmitButtonProps {
  onClick: () => void;
  activity: Activity;
  isFixed?: boolean;
}

export const SubmitButton = memo(function SubmitButton({ onClick, activity, isFixed = false }: SubmitButtonProps) {
  const t = useTranslations();

  const { userInfo, isTeamSpace } = useStore((state) => ({
    userInfo: state.userInfo,
    isTeamSpace: state.isTeamSpace,
  }));
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const pathname = usePathname();
  const isCompetition = activity.type === ActivityType.Competition;
  const isInSubmission = activity.phase === 'in_submission';

  const handleClick = useCallback(() => {
    if (userInfo) {
      onClick();
      reportEvent('submit_click', {
        index: 0,
        activity_id: activity.id || '',
      });
    } else {
      openLoginDialog();
      reportEvent('login_pop_exposure', {
        refer_element: 'activity - submit-button',
        refer_page: pathname,
      });
    }
  }, [userInfo, onClick, openLoginDialog, activity.id, pathname]);

  useEffect(() => {
    reportEvent('submit_show', {
      index: 0,
      activity_id: activity.id || '',
    });
  }, [activity.id]);

  return (
    isCompetition &&
    isInSubmission &&
    !isTeamSpace && (
      <div
        className={cn(
          'inline-flex h-[40px] cursor-pointer items-center justify-center gap-2',
          'rounded-[10px] bg-gradient-to-l from-[#93d7ff] to-[#dfddff] px-6 text-black text-sm',
          'transition-opacity hover:opacity-90',
          isFixed
            ? 'fixed right-0 bottom-[calc(env(safe-area-inset-bottom)+16px)] left-0 z-10 mx-auto w-[calc(100%-48px)]'
            : 'md:mx-4 md:w-full',
        )}
        onClick={handleClick}
      >
        {t('activity.submit')}
      </div>
    )
  );
});
